import { request } from './client';
import type { SourceStatus } from '../connectors/registry';

// ── Wire types (mirror OnboardingEndpoints.cs) ──────────────────────────────

export interface MappingRule {
  id: string;
  name: string;
  kind: 'identifier_in_field' | 'value_to_target' | 'attribute_join';
  field: string;
  pattern?: string | null;
  terms?: string[] | null;
  wbs1?: string | null;
  wbs2?: string | null;
  wbs3?: string | null;
  enabled?: boolean;
  sampleHits?: number;
  sampleSize?: number;
}

export interface CatalogueScan {
  projectCount: number;
  withClientName: number;
  distinctOrgs: number;
}

export interface SettleScan {
  sampled: number;
  settled: number;
  shortlistLe3: number;
}

export interface SourceSampleScan {
  source: string;
  eventCount: number;
  withTags: number;
  tagsResolved: number;
  subjectsWithProjectCode: number;
  exampleSubject: string | null;
}

export interface SourceScanEntry {
  id: string;
  status: SourceStatus;
  sample: SourceSampleScan | null;
}

export interface ScanResponse {
  catalogue: CatalogueScan;
  baseline: SettleScan;
  withRules: SettleScan | null;
  sources: SourceScanEntry[];
  suggestedOrgJoin: MappingRule | null;
}

export interface CompileResponse {
  mechanizable: boolean;
  rules: MappingRule[];
  flagNote: string | null;
}

export interface RuleValidation {
  ruleId: string;
  hits: number;
  sampleSize: number;
  unresolvedCodes: string[];
  // How many sampled events carried anything in the field the rule reads —
  // separates "nothing has tags" from "they have tags and none matched".
  fieldPresent: number;
}

export interface ValidateResponse {
  results: RuleValidation[];
  estimate: SettleScan;
}

export interface RulesResponse {
  rules: MappingRule[];
  commentTemplate: string | null;
  flaggedNotes: string[];
  agentContext: string | null;
}

// The proof the tenant's guidance buys (ADR-0045 §6). `ambiguous` is the count
// of sampled events the deterministic layer can't confidently place — the set
// the agent decides. `placedWithout` / `placedWith` are how many of those the
// agent confidently places without the guidance and with it.
export interface PreviewContextResponse {
  sampled: number;
  ambiguous: number;
  placedWithout: number;
  placedWith: number;
}

// ── Calls ───────────────────────────────────────────────────────────────────

export function getOnboardingScan(tz: string): Promise<ScanResponse> {
  return request<ScanResponse>(`/api/onboarding/scan?tz=${encodeURIComponent(tz)}`);
}

export function compileAnswer(questionKey: string, answer: string): Promise<CompileResponse> {
  return request<CompileResponse>('/api/onboarding/compile', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ questionKey, answer }),
  });
}

export function validateRules(
  rules: MappingRule[], source: string | null, tz: string,
): Promise<ValidateResponse> {
  return request<ValidateResponse>('/api/onboarding/validate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rules, source, tz }),
  });
}

export function previewAgentContext(
  agentContext: string, tz: string,
): Promise<PreviewContextResponse> {
  return request<PreviewContextResponse>('/api/onboarding/preview-context', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ agentContext, tz }),
  });
}

export function getOnboardingRules(): Promise<RulesResponse> {
  return request<RulesResponse>('/api/onboarding/rules');
}

export function saveOnboardingRules(payload: {
  rules: MappingRule[];
  commentTemplate: string | null;
  flaggedNotes: string[];
  agentContext: string | null;
}): Promise<void> {
  return request<void>('/api/onboarding/rules', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}
